import {
    HttpEvent,
    HttpHandler,
    HttpInterceptor,
    HttpRequest,
    HttpResponse,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { KeycloakService } from '@inoventoryapps/keycloak-angular';
import { Observable } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { KeyService } from '../_services/_utils/key.service';
import { SERVICE_URL } from '../_urls/service.urls';

@Injectable()
export class EncryptionInterceptor implements HttpInterceptor {
    private readonly excludedRoutes = new Set([
        SERVICE_URL.REPORTS,
        'lokos-transaction/master/organization/federation',
        'lokos-transaction/clf/memberLoanDetailsReceipt',
        'search/lab/disbursements-history',
        'transactions/v3/lokos-transaction/clf/group-receipts',
    ]);

    constructor(
        private keyService: KeyService,
        private keyclockService: KeycloakService
    ) {}

    /**
     * For encryption and decryption of data
     * @param req
     * @param next
     * @returns encrypted data
     */
    intercept(
        req: HttpRequest<any>,
        next: HttpHandler
    ): Observable<HttpEvent<any>> {
        return new Observable((observer) => {
            this.keyService.getClientKey().then(async () => {
                try {
                    console.log(
                        '[API PAYLOAD - Decrypted]',
                        req.method,
                        req.url,
                        req.body
                    );
                    const url = req.url.includes(SERVICE_URL.KEYCLOCK_SERVICE)
                        ? environment.keyClockApiBaseUrl
                        : environment.fdmApiBaseUrl;
                    const serverPublicKey =
                        await this.keyService.getServerPublicKey(
                            `${url}${SERVICE_URL.CRYPTO_JWKS}`,
                            req.url
                        );
                    let encryptedBody: any;
                    if (this.isByPass(req)) {
                        await this.keyclockService
                            .encrypt({ publicKey: serverPublicKey }, req.body)
                            .then((encryptedData: any) => {
                                encryptedBody = encryptedData;
                            });
                    } else {
                        encryptedBody = req.body;
                    }

                    let setHeaders: any = {
                        'x-encrypted': this.isByPass(req) ? 'true' : 'false',
                    };
                    if (!req.url.includes('signer')) {
                        setHeaders = {
                            ...setHeaders,
                            DPoP: await this.keyclockService.dpopProof(
                                req.url,
                                req.method,
                                encryptedBody?.iv || '',
                                this.keyService.getKid(req.url)
                            ),
                        };
                    }
                    console.log(
                        '[API Payload - Decrypted]',
                        req.method,
                        req.url,
                        JSON.stringify(req.body)
                    );
                    const newReq = req.clone({
                        body: this.isByPass(req)
                            ? { data: encryptedBody.encrypted }
                            : req.body,
                        setHeaders: {
                            ...setHeaders,
                        },
                    });

                    next.handle(newReq)
                        .pipe(
                            switchMap(async (event: HttpEvent<any>) => {
                                if (
                                    event instanceof HttpResponse &&
                                    event.headers.get('x-encrypted') === 'true'
                                ) {
                                    if (event.body && event.body['data']) {
                                        const decodedBody =
                                            await this.keyclockService.decrypt(
                                                { publicKey: serverPublicKey },
                                                event.body['data'],
                                                event.headers.get(
                                                    'x-crypto-enabled'
                                                )
                                            );
                                        console.log(
                                            '[API Response - Decrypted]',
                                            req.method,
                                            req.url,
                                            decodedBody
                                        );
                                        observer.next(
                                            event.clone({ body: decodedBody })
                                        );
                                    } else {
                                        console.log(
                                            '[API Response - Empty]',
                                            req.method,
                                            req.url
                                        );
                                        observer.next(
                                            event.clone({ body: {} })
                                        );
                                    }
                                } else if (event instanceof HttpResponse) {
                                    console.log(
                                        '[API Response - Plain]',
                                        req.method,
                                        req.url,
                                        event.body
                                    );
                                    // console.log(JSON.stringify(event.body));
                                    observer.next(event);
                                } else {
                                    observer.next(event);
                                }
                            }),
                            catchError((error) => {
                                observer.error(error);
                                return [];
                            })
                        )
                        .subscribe({
                            complete: () => observer.complete(),
                        });
                } catch (error) {
                    observer.error(error);
                }
            });
        });
    }

    isByPass(req: HttpRequest<any>): boolean {
        if (!environment.enableEncryption) {
            return false;
        }
        return !Array.from(this.excludedRoutes).some((route) =>
            req.url.includes(route)
        );
    }
}
